import { useEffect, useRef, useState } from 'react';
import { GRADING_CANVAS_WIDE_MIN_WIDTH } from '../../../../../../shared/constants.js';

const WIDE_CANVAS_MIN_WIDTH = GRADING_CANVAS_WIDE_MIN_WIDTH;

export function useGradingDom() {
  const headerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const layoutContainerRef = useRef<HTMLDivElement>(null);

  const isGrammarModeRef = useRef(false);

  const [isCompact, setIsCompact] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isTaskDropdownOpen, setIsTaskDropdownOpen] = useState(false);
  const [isCanvasNarrow, setIsCanvasNarrow] = useState(true);

  // Measure Header Width for Compact Mode
  useEffect(() => {
    if (!headerRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) setIsCompact(entry.contentRect.width < 450);
    });
    observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, []);

  // Measure Layout Container Width for Wide Canvas Mode
  useEffect(() => {
    const node = layoutContainerRef.current;
    if (!node) return;

    const updateLayoutMode = () => {
      const width = node.getBoundingClientRect().width;
      setIsCanvasNarrow(width < WIDE_CANVAS_MIN_WIDTH);
    };

    updateLayoutMode();
    const observer = new ResizeObserver(() => updateLayoutMode());
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  // Handle Wheel Zoom — ignored in grammar mode to avoid silently changing the correction zoom
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        if (!isGrammarModeRef.current) {
          setZoom(prev => Math.min(Math.max(0.5, prev + (e.deltaY > 0 ? -0.1 : 0.1)), 3));
        }
      }
    };
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleWheel);
  }, []);

  // Handle Click Outside Dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsTaskDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return {
    refs: { headerRef, dropdownRef, imageContainerRef, layoutContainerRef, isGrammarModeRef },
    state: { isCompact, zoom, isTaskDropdownOpen, isCanvasNarrow },
    actions: { setIsCompact, setZoom, setIsTaskDropdownOpen, setIsCanvasNarrow },
  };
}
